import type { SupabaseClient } from '@supabase/supabase-js';
import type { BoardChange, BoardRealtime, ChannelRealtime, Unsubscribe } from '../ports.js';
import type { StaffMessage } from '../domain.js';
import { rowToStaffMessage } from '../rows.js';

/**
 * Supabase Realtime adapters, with the BROWSER's authed client (RLS applies to
 * the replication stream too — a subscriber sees only rows it could read).
 *
 * THE TABLE NAMES LIVE HERE. Four views each listed the same four board tables
 * inline; a table rename meant a component sweep, and the `staff_messages`
 * listeners went further and handed the raw PostgREST payload to the view to
 * map.
 */

/** Which board tables map to which domain signal (`BoardChange`). */
const BOARD_TABLES: { table: string; change: BoardChange }[] = [
  { table: 'board_activity_presence', change: 'presence' },
  { table: 'board_absence', change: 'presence' },
  { table: 'board_days', change: 'demand' },
  { table: 'board_activity_overrides', change: 'demand' },
];

/**
 * Channel names must be unique per client connection, and the previous code
 * used FIXED ones (`'reception-board'`, `'board-presence-feed'`). Two
 * simultaneous subscribers in one app — a second board view, a remount before
 * teardown completes — would collide on the same name. A per-subscription
 * counter removes that failure mode entirely; nothing depends on the name.
 */
let channelSeq = 0;

export class SupabaseBoardRealtime implements BoardRealtime {
  constructor(private readonly db: SupabaseClient) {}

  watchBoard(onChange: (what: BoardChange) => void): Unsubscribe {
    let channel = this.db.channel(`board-${++channelSeq}`);
    for (const { table, change } of BOARD_TABLES) {
      channel = channel.on('postgres_changes', { event: '*', schema: 'public', table }, () =>
        onChange(change),
      );
    }
    channel.subscribe();
    return () => void this.db.removeChannel(channel);
  }
}

export class SupabaseChannelRealtime implements ChannelRealtime {
  constructor(private readonly db: SupabaseClient) {}

  watchNewMessages(onInsert: (message: StaffMessage) => void): Unsubscribe {
    const channel = this.db
      .channel(`staff-messages-${++channelSeq}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'staff_messages' },
        // Mapped HERE, so the view never touches a wire row.
        (payload) => onInsert(rowToStaffMessage(payload.new as never)),
      )
      .subscribe();
    return () => void this.db.removeChannel(channel);
  }
}
